/**
 * Local Browser Host
 *
 * Manages a local browser instance and handles commands from the cloud API
 * via WebSocket. Owns the WebSocket *client* transport (dial-out to our API)
 * and delegates all browser automation to a `BrowserCommandExecutor`, which
 * wraps `PlaywrightClient` from @chatsdet/browser-core — ensuring the agent
 * experience is identical regardless of whether the browser is local or
 * cloud.
 *
 * @module local-browser-host
 */

import type { PlaywrightClient } from '@chatsdet/browser-core';
import { BrowserCommandExecutor } from './executor.js';
import type {
  BrowserCommand,
  BrowserResponse,
  HeartbeatMessage,
  SessionMessage,
  LocalBrowserMode,
} from './protocol';

const HEARTBEAT_INTERVAL_MS = 30_000;
const RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

interface LocalBrowserHostOptions {
  apiUrl: string;
  wsToken: string;
  sessionId: string;
  browserMode: LocalBrowserMode;
  cdpUrl?: string;
  headless?: boolean;
  storageStatePath?: string;
  onLog?: (level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: unknown) => void;
  /** Inject an existing PlaywrightClient instead of creating a new one */
  existingClient?: PlaywrightClient;
  /** When true, skip browser launch in start() — used when browser is already managed externally */
  skipBrowserLaunch?: boolean;
  /** Called before executing a command — return false to reject with a 'paused' error */
  shouldExecute?: () => boolean;
}

/**
 * LocalBrowserHost manages the WebSocket connection to the cloud API and
 * delegates all browser operations to a BrowserCommandExecutor backed by a
 * shared PlaywrightClient instance.
 */
export class LocalBrowserHost {
  private options: LocalBrowserHostOptions;
  private ws: WebSocket | null = null;
  private executor: BrowserCommandExecutor;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private isShuttingDown = false;

  constructor(options: LocalBrowserHostOptions) {
    this.options = options;
    this.executor = new BrowserCommandExecutor({
      browserMode: options.browserMode,
      cdpUrl: options.cdpUrl,
      headless: options.headless,
      storageStatePath: options.storageStatePath,
      existingClient: options.existingClient,
      skipBrowserLaunch: options.skipBrowserLaunch,
      logger: (level, message, data) => this.log(level, message, data),
    });
  }

  private log(level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: unknown) {
    if (this.options.onLog) {
      this.options.onLog(level, message, data);
    } else {
      const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      fn(`[LocalBrowserHost] ${message}`, data ?? '');
    }
  }

  // =========================================================================
  // Lifecycle
  // =========================================================================

  async start(): Promise<void> {
    this.log('info', 'Starting local browser host', {
      browserMode: this.options.browserMode,
      sessionId: this.options.sessionId,
    });

    // Connect to WebSocket first
    await this.connectWebSocket();

    // Launch browser via the command executor (owns the PlaywrightClient lifecycle)
    await this.executor.start();

    // Notify cloud that browser is ready
    this.sendSessionEvent('browser_ready');
  }

  async stop(): Promise<void> {
    this.isShuttingDown = true;
    this.log('info', 'Stopping local browser host');

    this.stopHeartbeat();

    if (this.ws) {
      try {
        this.ws.close(1000, 'Shutdown');
      } catch {
        /* intentionally empty */
      }
      this.ws = null;
    }

    await this.executor.stop();
    this.log('info', 'Local browser host stopped');
  }

  // =========================================================================
  // WebSocket Connection
  // =========================================================================

  private async connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      const wsUrl = `${this.options.apiUrl.replace('http', 'ws')}/local-browser/sessions/${this.options.sessionId}/connect?token=${this.options.wsToken}`;

      this.log('info', 'Connecting to cloud API', { url: wsUrl.replace(/token=.*/, 'token=***') });

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        this.log('info', 'Connected to cloud API');
        this.ws = ws;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        resolve();
      };

      ws.onmessage = (event) => {
        this.handleMessage(event.data as string);
      };

      ws.onerror = (event) => {
        this.log('error', 'WebSocket error', event);
      };

      ws.onclose = () => {
        this.log('info', 'WebSocket closed');
        this.stopHeartbeat();
        this.ws = null;

        if (!this.isShuttingDown) {
          this.scheduleReconnect();
        }
      };

      // Timeout after 30 seconds
      setTimeout(() => {
        if (!this.ws) {
          reject(new Error('WebSocket connection timeout'));
        }
      }, 30_000);
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.log('error', 'Max reconnection attempts reached, giving up');
      this.stop();
      return;
    }

    const delay = Math.min(
      RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts),
      MAX_RECONNECT_DELAY_MS
    );

    this.reconnectAttempts++;
    this.log('info', `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(async () => {
      try {
        await this.connectWebSocket();
        this.sendSessionEvent('connected');
        this.sendSessionEvent('browser_ready');
      } catch (error) {
        this.log('error', 'Reconnection failed', error);
        this.scheduleReconnect();
      }
    }, delay);
  }

  // =========================================================================
  // Heartbeat
  // =========================================================================

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        const ping: HeartbeatMessage = {
          type: 'heartbeat',
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          direction: 'pong',
        };
        this.ws.send(JSON.stringify(ping));
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // =========================================================================
  // Message Handling
  // =========================================================================

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      if (message.type === 'heartbeat' && message.direction === 'ping') {
        const pong: HeartbeatMessage = {
          type: 'heartbeat',
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          direction: 'pong',
        };
        this.ws?.send(JSON.stringify(pong));
        return;
      }

      if (message.type === 'command') {
        this.handleCommand(message as BrowserCommand);
        return;
      }

      this.log('debug', 'Received unknown message type', message);
    } catch (error) {
      this.log('error', 'Failed to parse message', { error, data });
    }
  }

  private async handleCommand(command: BrowserCommand): Promise<void> {
    const startTime = Date.now();

    // Check if execution is allowed (e.g. session may be paused by CLI taking control)
    if (this.options.shouldExecute && !this.options.shouldExecute()) {
      const response: BrowserResponse = {
        type: 'response',
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        requestId: command.id,
        success: false,
        error: 'paused',
        contextId: command.contextId,
      };
      this.ws?.send(JSON.stringify(response));
      this.log('debug', `Command rejected (paused): ${command.method}`, { id: command.id });
      return;
    }

    this.log('debug', `Executing command: ${command.method}`, { id: command.id });

    try {
      const result = await this.executor.execute(command.method, command.args);
      const response: BrowserResponse = {
        type: 'response',
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        requestId: command.id,
        success: true,
        result,
        contextId: command.contextId,
      };
      this.ws?.send(JSON.stringify(response));

      this.log('debug', `Command completed: ${command.method}`, {
        id: command.id,
        durationMs: Date.now() - startTime,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const response: BrowserResponse = {
        type: 'response',
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        requestId: command.id,
        success: false,
        error: errorMessage,
        stack: error instanceof Error ? error.stack : undefined,
        contextId: command.contextId,
      };
      this.ws?.send(JSON.stringify(response));

      this.log('error', `Command failed: ${command.method}`, {
        id: command.id,
        error: errorMessage,
      });
    }
  }

  private sendSessionEvent(
    event: 'connected' | 'disconnected' | 'browser_ready' | 'browser_closed' | 'error',
    error?: string
  ): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const message: SessionMessage = {
      type: 'session',
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      event,
      browserMode: this.options.browserMode,
      error,
    };
    this.ws.send(JSON.stringify(message));
  }
}
